import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useConsultation } from '../contexts/ConsultationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Search, Plus, X, FileText, ChevronDown, Save, Zap, Upload, FlaskConical } from 'lucide-react';
import DrawerShell from './DrawerShell';
import { Label } from '../components/ui/label';
import axios from 'axios';
import VoiceDictationButton, { VoiceConfirmModal } from './VoiceDictationButton';
import TestReportDrawer from './TestReportDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function InvestigationsSection({ symptomTags = [] }) {
  const { consultation, saveDraft } = useConsultation();
  const [selectedTests, setSelectedTests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  // Report-viewer drawer: tracks which test we're inspecting reports for.
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [reportDrawerTest, setReportDrawerTest] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [quickAdds, setQuickAdds] = useState([]);
  const [quickAddSource, setQuickAddSource] = useState('');
  // Voice dictation — `voicePayload` holds Claude's parsed test names
  // while the doctor reviews them in the confirm modal.
  const [voicePayload, setVoicePayload] = useState(null);
  const searchRef = useRef(null);
  const templateDropdownRef = useRef(null);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/templates`, { withCredentials: true });
        setTemplates(data.filter(t => t.content?.investigations?.length > 0));
      } catch (e) { /* ignore */ }
    };
    loadTemplates();
  }, []);

  // Initialize from consultation.
  //
  // Defensive coercion: very old / mis-saved records may carry
  // investigation items as objects like `{name: "CBC", master_match: …}`
  // (a regression from the early full-visit voice flow). Rendering an
  // object as a React child throws #31 ("Objects are not valid as a
  // React child"), which crashed the entire consultation screen. We
  // normalise everything to plain strings on load so the existing chip
  // renderer keeps working AND the very next saveDraft flushes the
  // healed shape back to the DB.
  useEffect(() => {
    const raw = consultation?.sections?.investigations?.items;
    if (!Array.isArray(raw)) return;
    const normalised = raw
      .map((t) => {
        if (typeof t === 'string') return t;
        if (t && typeof t === 'object') return t.name || t.test_name || '';
        return '';
      })
      .filter(Boolean);
    setSelectedTests(normalised);
    // If the on-disk shape was dirty, write the healed copy back so the
    // next render of any view (drawer / print / past-visit) sees clean
    // strings instead of objects.
    const hadObjects = raw.some((t) => t && typeof t === 'object');
    if (hadObjects) {
      saveDraft('investigations', normalised);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultation?.id]);

  // Close template dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setShowTemplateDropdown(false);
        setTemplatePreview(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load quick-add investigation suggestions based on symptoms
  useEffect(() => {
    const loadQuickAdds = async () => {
      try {
        const symptomsStr = consultation?.sections?.chief_complaints?.content || '';
        const { data } = await axios.get(`${API_URL}/api/quick-adds`, {
          params: { symptoms: symptomsStr, section_type: 'investigations' },
          withCredentials: true
        });
        setQuickAdds(data.investigations || []);
        setQuickAddSource(data.source || '');
      } catch (e) { /* ignore */ }
    };
    loadQuickAdds();
  }, [consultation?.sections?.chief_complaints?.content]);

  // Live search investigations from external DB
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout) clearTimeout(searchTimeout);
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/common/tests`, {
          params: { search: query },
          withCredentials: true
        });
        // Filter out already selected
        setSearchResults(data.filter(t => !selectedTests.includes(t.name)));
      } catch (e) {
        console.error('Investigation search error:', e);
      }
    }, 300);
    setSearchTimeout(timeout);
  };

  const addTest = useCallback((name) => {
    // Callers occasionally pass a structured suggestion object
    // ({name, reason, priority}) instead of a plain string — e.g. the
    // Yanthra AI "Adopt All" path in ClinicalAICopilot. Coerce here so
    // we never push a non-renderable object into `selectedTests` (which
    // would later crash React with "Objects are not valid as a React
    // child" — see prod incident #31).
    if (name && typeof name === 'object') {
      name = name.name || name.test_name || '';
    }
    if (!name || typeof name !== 'string') return;
    // Functional update so back-to-back synchronous adds (e.g. when the AI
    // panel applies a template via forEach) don't read a stale `prev`
    // closure and overwrite each other — the previous non-functional
    // implementation caused only the last item of a template to land.
    let nextList = null;
    setSelectedTests(prev => {
      if (prev.includes(name)) {
        nextList = prev;
        return prev;
      }
      nextList = [...prev, name];
      return nextList;
    });
    if (nextList) saveDraft('investigations', nextList);
    setSearchQuery('');
    setSearchResults([]);
    searchRef.current?.focus();
  }, [saveDraft]);

  // Bulk-add helper used by the AI panel when applying a saved template.
  // Adds every test in one setState call so we avoid the per-item race
  // entirely and only fire ONE saveDraft round-trip.
  const addManyTests = useCallback((names) => {
    if (!Array.isArray(names) || names.length === 0) return;
    // Same defensive coercion as addTest — accept objects {name,...} too.
    const normalised = names
      .map((n) => (n && typeof n === 'object') ? (n.name || n.test_name || '') : n)
      .filter((n) => typeof n === 'string' && n.length > 0);
    if (normalised.length === 0) return;
    let nextList = null;
    setSelectedTests(prev => {
      const seen = new Set(prev);
      const additions = normalised.filter(n => !seen.has(n));
      if (additions.length === 0) {
        nextList = prev;
        return prev;
      }
      nextList = [...prev, ...additions];
      return nextList;
    });
    if (nextList) saveDraft('investigations', nextList);
  }, [saveDraft]);

  const removeTest = useCallback((name) => {
    let nextList = null;
    setSelectedTests(prev => {
      nextList = prev.filter(t => t !== name);
      return nextList;
    });
    if (nextList) saveDraft('investigations', nextList);
  }, [saveDraft]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      addTest(searchResults[0].name);
    }
  };

  // Voice dictation review pipeline: payload holds Claude's parsed tests
  // until the doctor explicitly confirms. Then we bulk-add through the
  // existing `addManyTests` helper so dedupe + saveDraft stay consistent.
  const handleVoiceConfirm = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const names = items.map((t) => (typeof t === 'string' ? t : (t.name || t.test_name || ''))).filter(Boolean);
    if (names.length === 0) return;
    addManyTests(names);
  }, [addManyTests]);

  // Template: load preview
  const loadTemplate = (template) => {
    setTemplatePreview(template);
  };

  const addAllFromTemplate = () => {
    if (!templatePreview) return;
    addManyTests(templatePreview.content.investigations || []);
    setTemplatePreview(null);
    setShowTemplateDropdown(false);
  };

  const addSingleFromTemplate = (testName) => {
    addTest(testName);
  };

  // Expose for AI panel
  useEffect(() => {
    window.addInvestigationFromSuggestion = addTest;
    window.addManyInvestigationsFromSuggestion = addManyTests;
    return () => {
      delete window.addInvestigationFromSuggestion;
      delete window.addManyInvestigationsFromSuggestion;
    };
  }, [addTest, addManyTests]);

  // Save template
  const handleSaveTemplate = async () => {
    if (!templateName.trim() || selectedTests.length === 0) return;
    setSavingTemplate(true);
    try {
      const templateData = {
        name: templateName.trim(),
        category: templateCategory.trim().toLowerCase() || 'custom',
        content: { investigations: selectedTests }
      };
      await axios.post(`${API_URL}/api/templates`, templateData, { withCredentials: true });
      // Reload templates
      const { data } = await axios.get(`${API_URL}/api/templates`, { withCredentials: true });
      setTemplates(data.filter(t => t.content?.investigations?.length > 0));
      setShowSaveTemplate(false);
      setTemplateName('');
      setTemplateCategory('');
    } catch (error) {
      console.error('Failed to save template:', error);
    }
    setSavingTemplate(false);
  };

  // Group selected tests by department
  const groupedTests = selectedTests.reduce((acc, testName) => {
    acc['Selected'] = acc['Selected'] || [];
    acc['Selected'].push(testName);
    return acc;
  }, {});

  return (
    <div className="space-y-2 h-full flex flex-col" data-testid="investigations-section">
      {/* Top bar: Grouped template actions + Search */}
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
        {/* Template group */}
        <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-0.5 bg-gray-50" ref={templateDropdownRef}>
          <div className="relative">
            <Button variant="ghost" size="sm"
              onClick={() => { setShowTemplateDropdown(!showTemplateDropdown); setTemplatePreview(null); }}
              className="gap-1 h-8 text-xs px-2" data-testid="inv-template-dropdown-btn">
              <FileText className="h-3.5 w-3.5" /> Quick Additions <ChevronDown className="h-3 w-3" />
            </Button>

            {showTemplateDropdown && (
              <div className="absolute z-50 mt-1 w-[360px] bg-white border border-gray-200 rounded-lg shadow-xl" data-testid="inv-template-dropdown-list">
                {templates.length === 0 ? (
                  <div className="p-3 text-center text-gray-400 text-sm">No templates yet</div>
                ) : templatePreview ? (
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-sm">{templatePreview.name}</p>
                        <Badge variant="secondary" className="text-xs">{templatePreview.category}</Badge>
                      </div>
                      <Button size="sm" onClick={addAllFromTemplate} className="bg-blue-700 hover:bg-blue-800 gap-1 h-7 text-xs" data-testid="add-all-inv-template">
                        <Plus className="h-3 w-3" /> Add All
                      </Button>
                    </div>
                  <ScrollArea className="max-h-48">
                    <div className="space-y-1">
                      {templatePreview.content.investigations.map((test, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                          <span className="font-medium">{test}</span>
                          <Button size="sm" variant="ghost" onClick={() => addSingleFromTemplate(test)}
                            className="h-7 px-2" data-testid={`add-single-inv-${i}`}
                            disabled={selectedTests.includes(test)}>
                            {selectedTests.includes(test) ? <span className="text-blue-700 text-xs">Added</span> : <Plus className="h-3 w-3" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <Button variant="ghost" size="sm" onClick={() => setTemplatePreview(null)} className="mt-2 w-full text-gray-500">
                    Back to templates
                  </Button>
                </div>
              ) : (
                <ScrollArea className="max-h-64">
                  {templates.map((tpl) => (
                    <button key={tpl.id} onClick={() => loadTemplate(tpl)}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 flex justify-between items-center border-b border-gray-100 last:border-0"
                      data-testid={`inv-template-option-${tpl.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-sm truncate">{tpl.name}</p>
                          {tpl.is_mine && (
                            <Badge variant="outline" className="text-[9px] h-4 px-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                              Yours
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{tpl.content.investigations.length} investigation(s)</p>
                      </div>
                      <Badge variant="secondary" className="text-xs">{tpl.category}</Badge>
                    </button>
                  ))}
                </ScrollArea>
              )}
            </div>
          )}
          </div>

          {selectedTests.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowSaveTemplate(true)}
              className="gap-1 h-8 text-xs px-2" data-testid="save-inv-template-btn">
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input ref={searchRef} value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={handleKeyDown} placeholder="Search investigations..."
            className="pl-7 h-8 text-xs" data-testid="investigation-search" />
          
          {searchResults.length > 0 && searchQuery && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-auto">
              {searchResults.map((test, i) => (
                <button key={i} type="button" onClick={() => addTest(test.name)}
                  className="w-full px-3 py-2 text-left hover:bg-blue-50 flex justify-between items-center border-b border-gray-100 last:border-0"
                  data-testid={`test-option-${i}`}>
                  <span className="font-medium text-xs">{test.name}</span>
                  <Badge variant="secondary" className="text-[10px]">{test.category}</Badge>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Voice dictation for tests */}
        <VoiceDictationButton
          mode="investigation"
          inventoryHint={[
            ...quickAdds,
            ...searchResults.map((r) => r.name),
          ].filter(Boolean)}
          onParsed={(payload) => setVoicePayload(payload)}
          size="sm"
          testId="voice-investigation-btn"
        />
      </div>

      {/* Quick Add Suggestions */}
      {quickAdds.length > 0 && (
        <div className="px-1 py-2 border-b border-gray-100" data-testid="quick-add-investigations">
          <div className="flex items-center gap-2 mb-1.5">
            <Zap className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
              {quickAddSource === 'symptom_map' ? 'Based on symptoms' : 'Recently ordered'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {quickAdds.filter(qa => !selectedTests.includes(qa)).slice(0, 10).map((inv, i) => (
              <button key={i}
                onClick={() => addTest(inv)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
                data-testid={`quick-add-inv-${i}`}>
                <Plus className="h-3 w-3" /> {inv}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selected Tests */}
      <div className="flex-1 overflow-auto">
        {selectedTests.length === 0 ? (
          <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
            <p className="text-sm mb-1">No investigations added</p>
            <p className="text-xs mb-3">Search, load a template, or speak the test names</p>
            <div className="flex items-center justify-center gap-2">
              <VoiceDictationButton
                mode="investigation"
                inventoryHint={[...quickAdds, ...searchResults.map((r) => r.name)].filter(Boolean)}
                onParsed={(payload) => setVoicePayload(payload)}
                size="sm"
                label="Dictate Tests"
                testId="voice-investigation-btn-empty"
              />
            </div>
            <p className="text-[10px] text-indigo-500 mt-3 italic">
              <span className="font-semibold">Tip:</span> Say &ldquo;Order CBC, ESR, HbA1c, chest X-ray PA&rdquo;
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-gray-500 mb-2">{selectedTests.length} investigation(s) selected</div>
            <div className="flex flex-wrap gap-2">
              {selectedTests.map((test, index) => (
                <Badge key={index} variant="secondary"
                  className="px-3 py-2 text-sm gap-2 bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100"
                  data-testid={`selected-test-${index}`}>
                  {test}
                  <button
                    onClick={() => { setReportDrawerTest(test); setReportDrawerOpen(true); }}
                    className="hover:text-violet-700 ml-1 transition"
                    title="Upload / view AI-analysed reports"
                    data-testid={`upload-report-${index}`}
                  >
                    <Upload className="h-3 w-3" />
                  </button>
                  <button onClick={() => removeTest(test)} className="hover:text-red-600 ml-0.5" data-testid={`remove-test-${index}`}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save as Template Drawer */}
      <DrawerShell
        open={showSaveTemplate}
        onClose={() => setShowSaveTemplate(false)}
        eyebrow="Reusable Bundle"
        title="Save Investigation Template"
        subtitle={`${selectedTests.length} investigation(s) will be saved`}
        accent="#1d4ed8"
        icon={<FlaskConical className="h-5 w-5" />}
        width="md"
        testId="save-inv-template-dialog"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={!templateName.trim() || savingTemplate}
              className="bg-blue-700 hover:bg-blue-800" data-testid="save-inv-template-confirm">
              {savingTemplate ? 'Saving...' : 'Save Template'}
            </Button>
          </>
        }
      >
        <div className="p-5 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="inv-template-name">Template Name</Label>
            <Input id="inv-template-name" value={templateName} onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Diabetes workup" data-testid="inv-template-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inv-template-category">Category</Label>
            <Input id="inv-template-category" value={templateCategory} onChange={(e) => setTemplateCategory(e.target.value)}
              placeholder="e.g., diabetes, cardiac, routine" data-testid="inv-template-category-input" />
          </div>
        </div>
      </DrawerShell>

      {/* Voice dictation review modal — doctor verifies the parsed tests
          before they land in the selected list. */}
      <VoiceConfirmModal
        open={!!voicePayload}
        onClose={() => setVoicePayload(null)}
        payload={voicePayload}
        mode="investigation"
        onConfirm={handleVoiceConfirm}
      />

      {/* Test report viewer/uploader drawer — opens per investigation. */}
      <TestReportDrawer
        open={reportDrawerOpen}
        onClose={() => setReportDrawerOpen(false)}
        consultationId={consultation?.id}
        patientId={consultation?.patient_id}
        investigationName={reportDrawerTest}
      />
    </div>
  );
}
